/**
 * 3D terrain for the mission map (VLP-2300 WP-B).
 *
 * Elevation comes from the server's own same-origin Terrarium proxy (`/tiles/terrain/{z}/{x}/{y}.png`,
 * routes/tiles.ts → AWS Terrain Tiles, cached and included in the offline pack). No new third-party
 * origin is contacted from the browser, so the CSP (docs/SECURITY.md) is unchanged.
 *
 * Honesty rule: terrain is only switched on after a real DEM tile decodes for the view. When the proxy
 * answers 204/empty (offline, upstream down) or the source errors before any tile loads, the map stays
 * flat and says so ("Terrain unavailable — flat view"). Exaggeration is always 1: heights are real.
 */
import type { Map as MlMap, RasterDEMSourceSpecification } from 'maplibre-gl';

export const DEM_SOURCE_ID = 'apos-dem';
/** Offline packs carry Terrarium to z12 (routes/tiles.ts); deeper zooms overscale smoothly. */
export const DEM_MAX_ZOOM = 12;
/** Zoom of the single tile probed before terrain is switched on. */
export const DEM_PROBE_ZOOM = 10;
/** Source errors tolerated (with no tile decoded) before terrain falls back to flat. */
export const DEM_ERROR_LIMIT = 3;
export const DEM_ATTRIBUTION = 'Terrain: AWS Terrain Tiles (Mapzen Terrarium; USGS 3DEP, SRTM, GMTED, ETOPO1)';
export const TERRAIN_UNAVAILABLE_TEXT = 'Terrain unavailable — flat view';

export type ViewMode = '2d' | '3d';
/** off = 2D; probing = 3D waiting for the first tile; on = draped terrain; unavailable = 3D but flat. */
export type TerrainStatus = 'off' | 'probing' | 'on' | 'unavailable';

/** Absolute tile template (MapLibre fetches tiles from a worker, where a relative URL has no base). */
export function demTileTemplate(origin: string = typeof location !== 'undefined' ? location.origin : ''): string {
  return `${origin}/tiles/terrain/{z}/{x}/{y}.png`;
}

export function demSource(origin?: string): RasterDEMSourceSpecification {
  return {
    type: 'raster-dem',
    tiles: [demTileTemplate(origin)],
    tileSize: 256,
    encoding: 'terrarium',
    maxzoom: DEM_MAX_ZOOM,
    attribution: DEM_ATTRIBUTION,
  };
}

/** Slippy-map tile containing lon/lat at zoom z. */
export function tileFor(lon: number, lat: number, z: number): { z: number; x: number; y: number } {
  const n = 2 ** z;
  const clampedLat = Math.max(-85.0511, Math.min(85.0511, lat));
  const x = Math.min(n - 1, Math.max(0, Math.floor(((lon + 180) / 360) * n)));
  const r = (clampedLat * Math.PI) / 180;
  const y = Math.min(n - 1, Math.max(0, Math.floor(((1 - Math.log(Math.tan(r) + 1 / Math.cos(r)) / Math.PI) / 2) * n)));
  return { z, x, y };
}

type FetchLike = (url: string) => Promise<{ status: number; headers: { get(name: string): string | null }; arrayBuffer(): Promise<ArrayBuffer> }>;

/**
 * Does the proxy serve a real DEM tile here? True only for a 200 PNG with a body; the proxy answers
 * 204 (empty) when it is offline or the upstream fails, and 404 outside its zoom range.
 */
export async function probeDem(lon: number, lat: number, fetchImpl: FetchLike, origin?: string): Promise<boolean> {
  if (!Number.isFinite(lon) || !Number.isFinite(lat)) return false;
  const t = tileFor(lon, lat, DEM_PROBE_ZOOM);
  const url = demTileTemplate(origin).replace('{z}', String(t.z)).replace('{x}', String(t.x)).replace('{y}', String(t.y));
  try {
    const r = await fetchImpl(url);
    if (r.status !== 200) return false;
    const type = r.headers.get('content-type') ?? '';
    if (!/image\/png/i.test(type)) return false;
    const body = await r.arrayBuffer();
    return body.byteLength > 0;
  } catch {
    return false;
  }
}

/**
 * Drives terrain for one map: probe → add the DEM source + setTerrain, or fall back to flat. Survives
 * basemap switches (style reloads drop sources) via `reinstall()`.
 */
export class TerrainController {
  private status: TerrainStatus = 'off';
  private wanted = false;
  private errors = 0;
  private loadedTile = false;
  private probeSeq = 0;
  private probed: boolean | null = null;

  constructor(
    private map: MlMap,
    private onStatus: (s: TerrainStatus) => void,
    private fetchImpl: FetchLike = (u) => fetch(u, { credentials: 'omit' }),
  ) {
    map.on('error', this.onError);
    map.on('sourcedata', this.onSourceData);
  }

  get current(): TerrainStatus {
    return this.status;
  }

  /** Switch terrain on (3D) or off (2D). `offline` = the basemap already fell back to the offline style. */
  set(want: boolean, offline: boolean): void {
    this.wanted = want;
    if (!want) {
      this.remove();
      this.setStatus('off');
      return;
    }
    if (offline) {
      this.remove();
      this.setStatus('unavailable');
      return;
    }
    if (this.status === 'on' || this.status === 'probing') return;
    if (this.probed === true) {
      this.install();
      return;
    }
    this.setStatus('probing');
    const seq = ++this.probeSeq;
    const c = this.map.getCenter();
    void probeDem(c.lng, c.lat, this.fetchImpl).then((ok) => {
      if (seq !== this.probeSeq || !this.wanted) return;
      this.probed = ok;
      if (ok) this.install();
      else this.setStatus('unavailable');
    });
  }

  /** After a style reload: put the DEM back if terrain was on. */
  reinstall(offline: boolean): void {
    if (!this.wanted) return;
    if (offline) {
      this.setStatus('unavailable');
      return;
    }
    if (this.status === 'on') {
      this.status = 'probing';
      this.install();
    } else if (this.status === 'unavailable' && this.probed !== false) this.set(true, offline);
  }

  destroy(): void {
    this.probeSeq++;
    this.map.off('error', this.onError);
    this.map.off('sourcedata', this.onSourceData);
  }

  private install(): void {
    const m = this.map;
    try {
      if (!m.getSource(DEM_SOURCE_ID)) m.addSource(DEM_SOURCE_ID, demSource());
      this.errors = 0;
      this.loadedTile = false;
      m.setTerrain({ source: DEM_SOURCE_ID, exaggeration: 1 });
      this.setStatus('on');
    } catch {
      this.remove();
      this.setStatus('unavailable');
    }
  }

  private remove(): void {
    this.probeSeq++;
    const m = this.map;
    try {
      if (m.getTerrain()) m.setTerrain(null);
      if (m.getSource(DEM_SOURCE_ID)) m.removeSource(DEM_SOURCE_ID);
    } catch {
      /* style mid-reload: nothing to remove */
    }
  }

  private setStatus(s: TerrainStatus): void {
    if (s === this.status) return;
    this.status = s;
    this.onStatus(s);
  }

  private onSourceData = (e: { sourceId?: string; tile?: unknown }) => {
    if (e.sourceId === DEM_SOURCE_ID && e.tile) this.loadedTile = true;
  };

  private onError = (e: { sourceId?: string }) => {
    if (e.sourceId !== DEM_SOURCE_ID || this.status !== 'on') return;
    this.errors++;
    // tiles keep failing and none ever decoded: the DEM is not really there → honest flat view
    if (!this.loadedTile && this.errors >= DEM_ERROR_LIMIT) {
      this.remove();
      this.probed = false;
      this.setStatus('unavailable');
    }
  };
}
